
import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/components/auth/AuthProvider";
import { SecurityProvider } from "@/components/security/SecurityProvider";
import ProtectedRoute from "@/components/ProtectedRoute";

// Lazy loaded - Admin Layout (separates admin code from public website)
const Layout = lazy(() => import("./components/layout/Layout"));

// Lazy loaded pages - Admin
const Index = lazy(() => import("./pages/Index"));
const Auth = lazy(() => import("./pages/Auth"));
const Members = lazy(() => import("./pages/Members"));
const Events = lazy(() => import("./pages/Events"));
const Commissions = lazy(() => import("./pages/Commissions"));
const Announcements = lazy(() => import("./pages/Announcements"));
const Surveys = lazy(() => import("./pages/Surveys"));
const Finance = lazy(() => import("./pages/Finance"));
const Reports = lazy(() => import("./pages/Reports"));
const Settings = lazy(() => import("./pages/Settings"));
const WebsiteManagement = lazy(() => import("./pages/WebsiteManagement"));
const Demirbas = lazy(() => import("./pages/Demirbas"));
const Roles = lazy(() => import("./pages/Roles"));
const Approvals = lazy(() => import("./pages/Approvals"));
const SearchManagement = lazy(() => import("./pages/SearchManagement"));
const Marketplace = lazy(() => import("./pages/Marketplace"));
const ApiDocumentation = lazy(() => import("./pages/ApiDocumentation"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Lazy loaded - Member Portal
const MemberLayout = lazy(() => import("./components/layout/MemberLayout"));
const MemberPortal = lazy(() => import("./pages/MemberPortal"));
const MemberProfile = lazy(() => import("./pages/MemberProfile"));
const MemberAnnouncements = lazy(() => import("./pages/member/MemberAnnouncements"));
const MemberEvents = lazy(() => import("./pages/member/MemberEvents"));
const MemberReports = lazy(() => import("./pages/member/MemberReports"));
const MemberDues = lazy(() => import("./pages/member/MemberDues"));

// Lazy loaded - Website Pages
const WebsiteHome = lazy(() => import("./pages/website/WebsiteHome"));
const About = lazy(() => import("./pages/website/About"));
const Contact = lazy(() => import("./pages/website/Contact"));
const WebsiteAnnouncements = lazy(() => import("./pages/website/Announcements"));
const WebsiteEvents = lazy(() => import("./pages/website/Events"));
const News = lazy(() => import("./pages/website/News"));
const Gallery = lazy(() => import("./pages/website/Gallery"));
const Shop = lazy(() => import("./pages/website/Shop"));
const Donate = lazy(() => import("./pages/website/Donate"));
const Management = lazy(() => import("./pages/website/Management"));
const History = lazy(() => import("./pages/website/History"));
const Charter = lazy(() => import("./pages/website/Charter"));
const WebsiteCommissions = lazy(() => import("./pages/website/Commissions"));
const WomenBranch = lazy(() => import("./pages/website/WomenBranch"));
const YouthBranch = lazy(() => import("./pages/website/YouthBranch"));
const Branches = lazy(() => import("./pages/website/Branches"));
const AboutAlevism = lazy(() => import("./pages/website/AboutAlevism"));
const FaithPrinciples = lazy(() => import("./pages/website/FaithPrinciples"));
const DemirbasPublic = lazy(() => import("./pages/website/DemirbasPublic"));
const SurveyPublic = lazy(() => import("./pages/SurveyPublic"));
const MembershipApplication = lazy(() => import("./pages/MembershipApplication"));
const MembershipApplications = lazy(() => import("./pages/MembershipApplications"));

const queryClient = new QueryClient();

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <SecurityProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  {/* Auth Routes */}
                  <Route path="/auth" element={<Auth />} />
                  
            {/* Member Portal Routes */}
            <Route path="/member-portal" element={
              <ProtectedRoute>
                <MemberLayout>
                  <MemberPortal />
                </MemberLayout>
              </ProtectedRoute>
            } />
            <Route path="/profile" element={
              <ProtectedRoute>
                <MemberLayout>
                  <MemberProfile />
                </MemberLayout>
              </ProtectedRoute>
            } />
            <Route path="/member/announcements" element={
              <ProtectedRoute>
                <MemberLayout>
                  <MemberAnnouncements />
                </MemberLayout>
              </ProtectedRoute>
            } />
            <Route path="/member/events" element={
              <ProtectedRoute>
                <MemberLayout>
                  <MemberEvents />
                </MemberLayout>
              </ProtectedRoute>
            } />
            <Route path="/member/reports" element={
              <ProtectedRoute>
                <MemberLayout>
                  <MemberReports />
                </MemberLayout>
              </ProtectedRoute>
            } />
            <Route path="/member/dues" element={
              <ProtectedRoute>
                <MemberLayout>
                  <MemberDues />
                </MemberLayout>
              </ProtectedRoute>
            } />

            {/* Admin Dashboard Routes */}
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Layout>
                  <Index />
                </Layout>
              </ProtectedRoute>
            } />
                  
                  <Route path="/members" element={
                    <ProtectedRoute>
                      <Layout>
                        <Members />
                      </Layout>
                    </ProtectedRoute>
                  } />
                  
                  <Route path="/events" element={
                    <ProtectedRoute>
                      <Layout>
                        <Events />
                      </Layout>
                    </ProtectedRoute>
                  } />
                  
                  <Route path="/commissions" element={
                    <ProtectedRoute>
                      <Layout>
                        <Commissions />
                      </Layout>
                    </ProtectedRoute>
                  } />
                  
                  <Route path="/announcements" element={
                    <ProtectedRoute>
                      <Layout>
                        <Announcements />
                      </Layout>
                    </ProtectedRoute>
                  } />
                  
                  <Route path="/surveys" element={
                    <ProtectedRoute>
                      <Layout>
                        <Surveys />
                      </Layout>
                    </ProtectedRoute>
                  } />
                  
                  <Route path="/finance" element={
                    <ProtectedRoute>
                      <Layout>
                        <Finance />
                      </Layout>
                    </ProtectedRoute>
                  } />
                  
                  <Route path="/demirbas" element={
                    <ProtectedRoute>
                      <Layout>
                        <Demirbas />
                      </Layout>
                    </ProtectedRoute>
                  } />
                  
                  <Route path="/roles" element={
                    <ProtectedRoute>
                      <Layout>
                        <Roles />
                      </Layout>
                    </ProtectedRoute>
                  } />
                  
                  <Route path="/approvals" element={
                    <ProtectedRoute>
                      <Layout>
                        <Approvals />
                      </Layout>
                    </ProtectedRoute>
                  } />
                  
                  <Route path="/search-management" element={
                    <ProtectedRoute>
                      <Layout>
                        <SearchManagement />
                      </Layout>
                    </ProtectedRoute>
                  } />
                  
                  <Route path="/reports" element={
                    <ProtectedRoute>
                      <Layout>
                        <Reports />
                      </Layout>
                    </ProtectedRoute>
                  } />
                  
                  <Route path="/website-management" element={
                    <ProtectedRoute>
                      <Layout>
                        <WebsiteManagement />
                      </Layout>
                    </ProtectedRoute>
                  } />
                  
                  <Route path="/marketplace" element={
                    <ProtectedRoute>
                      <Marketplace />
                    </ProtectedRoute>
                  } />
                  
                  <Route path="/api-docs" element={
                    <ProtectedRoute>
                      <Layout>
                        <ApiDocumentation />
                      </Layout>
                    </ProtectedRoute>
                  } />
                  
                  <Route path="/settings" element={
                    <ProtectedRoute>
                      <Layout>
                        <Settings />
                      </Layout>
                    </ProtectedRoute>
                  } />

                  <Route path="/basvurular" element={
                    <ProtectedRoute>
                      <Layout>
                        <MembershipApplications />
                      </Layout>
                    </ProtectedRoute>
                  } />

                  {/* Public Website Routes - Main route now goes to website */}
                  <Route path="/" element={<WebsiteHome />} />
                  <Route path="/website" element={<WebsiteHome />} />
                  <Route path="/hakkimizda" element={<About />} />
                  <Route path="/yonetim-kurulu" element={<Management />} />
                  <Route path="/tarihcemiz" element={<History />} />
                  <Route path="/tuzugumuz" element={<Charter />} />
                  <Route path="/komisyonlarimiz" element={<WebsiteCommissions />} />
                  <Route path="/kadin-kollari" element={<WomenBranch />} />
                  <Route path="/genclik-kollari" element={<YouthBranch />} />
                  <Route path="/subelerimiz" element={<Branches />} />
                  <Route path="/alevilik-nedir" element={<AboutAlevism />} />
                  <Route path="/inanc-esaslari" element={<FaithPrinciples />} />
                  <Route path="/website-duyurular" element={<WebsiteAnnouncements />} />
                  <Route path="/website-etkinlikler" element={<WebsiteEvents />} />
                  <Route path="/haberler" element={<News />} />
                  <Route path="/galeri" element={<Gallery />} />
                  <Route path="/magaza" element={<Shop />} />
                  <Route path="/bagis" element={<Donate />} />
                  <Route path="/iletisim" element={<Contact />} />
                  <Route path="/demirbas-public/:code" element={<DemirbasPublic />} />

                  {/* Public PWA — Üyelik Başvuru Formu */}
                  <Route path="/uyelik-basvuru" element={<MembershipApplication />} />

                  {/* Public Survey Routes (HMAC-signed link) */}
                  <Route path="/s/:surveyId/:memberId" element={<SurveyPublic />} />
                  <Route path="/survey/:surveyId/:memberId" element={<SurveyPublic />} />
                  
                  {/* 404 */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </TooltipProvider>
          </SecurityProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
